import { Stack, TextField } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { PromptGroup_deprecated } from '@server/prompts';
import pluralize from 'pluralize';
import { DotBullet } from '@sweep-io/sweep-design/dist/icons';
import { useRef, useMemo, useEffect, useCallback } from 'react';
import { PromptCardActions } from './PromptCardActions';
import { CardIcon } from './CardIcon';
import { CardPin } from './CardPin';
import { CardLoader } from './CardLoader';
import { usePromptGroups } from '../../promptsLibrary/usePromptGroups';
import { usePromptLibraryCard } from './usePromptLibraryCard';
import { useSelector } from 'react-redux';
import { selectPromptGroupsWithPrompts_deprecated } from '../../../../reducers/promptLibraryReducer';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { ClampedText } from '../../../common/ClampedText';
import { TruncatedTextTooltip } from '../../../common/TruncatedTextTooltip';
import { StyledCard } from '../../promptsLibrary/minimized-groups/StyledCard';

interface PromptGroupCardContentProps {
  promptGroup: PromptGroup_deprecated;
  onClick?: (promptGroup: PromptGroup_deprecated) => void;
}

export const PromptGroupCardContent = ({ promptGroup, onClick }: PromptGroupCardContentProps) => {
  const { addNotification } = useSweepNotifications();
  const allPromptGroups = useSelector(selectPromptGroupsWithPrompts_deprecated);
  const pinnedGroups = useMemo(
    () => allPromptGroups?.filter((group) => group.isPinned).map((group) => group.id),
    [allPromptGroups],
  );

  const {
    promptGroupInEdit,
    showNameErrorState,
    onEdit,
    onConfirm,
    startEdit,
    cancelEdit,
    onDeleteCard,
    isCardLoading,
  } = usePromptLibraryCard({
    promptGroup,
  });

  const isEditable = !!promptGroupInEdit;
  const { pinGroup } = usePromptGroups();
  const _promptGroup = promptGroupInEdit ?? promptGroup;
  const { iconName, name, description, prompts } = _promptGroup;
  const nonEmptyPrompts = prompts?.filter((prompt) => prompt.id !== '');

  const cardRef = useRef<HTMLDivElement>(null);

  const onPinClick = (groupId: string, isPinned: boolean) => {
    if (!isPinned) {
      const groupsLeft = pinnedGroups?.filter((group) => group !== groupId);
      if (groupsLeft?.length === 0) {
        addNotification({
          message: 'There should be at least one pinned prompt category',
          variant: SweepNotificationVariant.Error,
        });
        return;
      }
    }
    pinGroup(groupId, isPinned);
  };

  // Handle click outside to confirm/cancel edit
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        onConfirm?.(_promptGroup);
      }
    },
    [onConfirm, _promptGroup],
  );

  useEffect(() => {
    if (!isEditable || isCardLoading) return;

    // Add listener after a short delay to avoid catching the edit button click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditable, isCardLoading, handleClickOutside]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation(); // Prevent dialog from closing
      cancelEdit();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      onConfirm?.(_promptGroup);
    }
  };

  const renderCardTitle = () => {
    if (isEditable) {
      return (
        <TextField
          value={name}
          onChange={(e) => {
            if (e.target.value.length <= 150) {
              onEdit('name', e.target.value);
            }
          }}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder="Add title"
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: colors.grey[100],
              minHeight: '32px',
              paddingRight: '6px',
            },

            '& fieldset': {
              borderColor: showNameErrorState ? colors.blush[500] + '!important' : undefined,
            },
          }}
          InputProps={{
            endAdornment: (
              <Stack direction="row" alignItems="center" sx={{ svg: { pl: 0.5 } }}>
                <Typography variant="tiny" whiteSpace="nowrap" color={colors.grey[500]}>
                  Max 150 characters
                </Typography>
                <DotBullet color={colors.blush[500]} />
              </Stack>
            ),
          }}
        />
      );
    }
    return <TruncatedTextTooltip variant="body-medium">{name}</TruncatedTextTooltip>;
  };

  const renderCardDescription = () => {
    if (isEditable) {
      return (
        <TextField
          value={description}
          onChange={(e) => onEdit('description', e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add description"
          sx={{
            mt: 0.5,
            '& .MuiInputBase-root': {
              backgroundColor: colors.grey[100],
            },
          }}
        />
      );
    }

    return (
      <ClampedText
        text={description ? description : 'Add description'}
        lines={3}
        typographyVariant="body"
        textColor={colors.grey[700]}
      />
    );
  };

  return (
    <StyledCard
      ref={cardRef}
      sx={{
        flex: 1,
        borderRadius: '8px',
        width: '100%',
        p: 2,
        position: 'relative',
        cursor: isEditable || isCardLoading ? 'default' : 'pointer',

        '&:hover .prompt-card-actions': {
          display: 'flex',
        },
      }}
      onClick={() => !isEditable && !isCardLoading && onClick?.(_promptGroup)}
    >
      {isCardLoading && <CardLoader />}

      <Stack direction="row" alignItems="center" justifyContent="space-between" pb={1}>
        <CardIcon icon={iconName} />

        <Stack direction="row" alignItems="center" gap={1}>
          <PromptCardActions
            onEdit={() => startEdit(_promptGroup)}
            onDelete={() => onDeleteCard?.(_promptGroup)}
            isDisabled={!!isCardLoading}
          />

          <CardPin
            isPinned={!!_promptGroup.isPinned}
            groupId={_promptGroup.id}
            onPinClick={onPinClick}
            isCardLoading={!!isCardLoading}
          />

          <Stack
            sx={{
              backgroundColor: colors.grey[150],
              borderRadius: '50px',
              p: '2px 6px',
            }}
          >
            <Typography variant="caption" color={colors.grey[800]}>
              {nonEmptyPrompts.length} {pluralize('Prompt', nonEmptyPrompts.length)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* pre-line so new line (/n) can be rendered properly */}
      <Stack gap={0.5} sx={{ whiteSpace: 'pre-line' }}>
        {renderCardTitle()}
        {renderCardDescription()}
      </Stack>
    </StyledCard>
  );
};
